'use client';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ImageProps } from '@repo/ui/src/types/image';
import Image from 'next/image';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useIsMD, useIsSM } from '@repo/ui/src/utils/useMediaQuery';

export interface TextIconSingleBoxes {
  label: string;
  name: string;
  icon: ImageProps;
  description?: string;
}

export interface TextIconComboBox {
  name: string;
  seperatorText?: string;
  content: Array<{ label: string; icon: any }>; //TODO: Type the icon prop better
}
export interface TextIconBoxesProps {
  textIconSingleBoxes: Array<TextIconSingleBoxes>;
  textIconComboBox: TextIconComboBox;
  darkMode?: boolean;
}

const TextIconBoxes = ({ textIconSingleBoxes, textIconComboBox, darkMode }: TextIconBoxesProps) => {
  const isMD = useIsMD();
  const isSM = useIsSM();
  const bgColor = darkMode ? 'bg-neutral-600' : 'bg-warm-200';

  const formattedSingleBoxes = textIconSingleBoxes?.map(({ name, ...rest }) => ({
    name,
    content: [rest],
  }));
  const combinedContentBoxes: Array<TextIconComboBox> = [textIconComboBox, ...formattedSingleBoxes];

  return (
    <ul
      className={classNames(
        'grid w-full md:w-auto justify-center grid-rows-[repeat(2, auto)] gap-6 mx-auto mt-18 lg:mt-20 list-none p-0',
        'md:order-2 xl:pt-10',
        isMD && 'grid-rows-[auto]',
      )}
      style={{
        gridTemplateColumns: isMD
          ? `max-content repeat(${textIconSingleBoxes.length}, auto)`
          : `repeat(${textIconSingleBoxes.length}, 1fr)`,
      }}
    >
      {combinedContentBoxes.map(({ name, seperatorText, content }, index) => (
        <li
          key={name}
          className={classNames(
            'flex flex-col py-4 px-5 sm:py-10 md:px-6',
            index === 0 && 'row-start-2 md:row-start-auto',
            bgColor,
          )}
          style={{
            gridColumn: index === 0 && !isMD ? `span ${textIconSingleBoxes.length}` : undefined,
          }}
        >
          <p
            className={classNames(
              'text-center lg:pb-6 md:mb-6 tra-text-web-heading-7',
              darkMode ? 'text-warm-400' : 'text-neutral-500',
            )}
          >
            {name}
          </p>

          <ul className="flex justify-center mt-3 md:mt-auto list-none p-0">
            {content.map(({ label, icon }, idx) => (
              <li key={label} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center w-8 h-8 sm:w-14 sm:h-14">
                    <Image
                      src={buildImageSrc(icon, isSM ? 56 : 32)}
                      width={isSM ? 56 : 32}
                      height={isSM ? 56 : 32}
                      objectFit="cover"
                      alt={icon.altTag}
                      className="w-full h-full max-w-14 max-h-14"
                      aria-hidden="true"
                    />
                  </div>
                  <p
                    className={classNames(
                      'pt-4 tra-text-web-heading-6 text-nowrap',
                      darkMode ? 'text-warm-400' : 'text-neutral-500',
                    )}
                  >
                    {label}
                  </p>
                </div>

                {content.length > 1 && idx < content.length - 1 ? (
                  <div className="relative mx-[5vw] sm:mx-12 w-px h-full bg-neutral-700">
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <p
                        className={classNames(
                          'tra-text-web-body-m mb-0 uppercase',
                          darkMode ? 'text-warm-400' : 'text-neutral-500',
                          bgColor,
                        )}
                      >
                        {seperatorText}
                      </p>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
};

export default TextIconBoxes;
